import { useMemo } from 'react';
import type { MapPoint } from '../../types';

/**
 * The map, behind one file.
 *
 * Two things are true at once and both matter:
 *
 *   1. Displaying a map is the cheap part. Google's mobile SDKs are free
 *      with no cap; the web gets 10,000 map loads a month free. What
 *      actually costs money is reverse geocoding, which is why the API
 *      resolves an address only when somebody opens a single visit.
 *
 *   2. A console that needs an API key before it shows anything is a
 *      console that is broken on day one, in every demo, and in every
 *      test environment.
 *
 * So the default renderer needs no key and no network: the points are
 * drawn to scale on their own bounding box, in order, with a distance
 * scale. It answers the question this screen is actually asked — did
 * this rep cross the city four times today, or work one neighbourhood? —
 * and it never fails to load.
 *
 * With VITE_MAPS_API_KEY set, swap the body of this component for the
 * provider's map. That is the whole change; nothing else imports a map.
 */

interface Props {
  points: MapPoint[];
  height?: number;
  selectedId?: string | null;
  onSelect?: (visitId: string) => void;
  /** Draw the order the stops were made in. */
  showPath?: boolean;
}

export default function MapCanvas({
  points, height = 420, selectedId, onSelect, showPath = true,
}: Props) {
  const plotted = points.filter(
    (p) => Number.isFinite(p.latitude) && Number.isFinite(p.longitude),
  );

  const geometry = useMemo(() => {
    if (!plotted.length) return null;

    const lats = plotted.map((p) => p.latitude);
    const lngs = plotted.map((p) => p.longitude);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    // Longitude degrees shrink towards the poles; at Mumbai's latitude a
    // degree of longitude is about 105 km against 111 km for latitude.
    // Ignoring that would stretch every day's route east-west.
    const midLat = (minLat + maxLat) / 2;
    const lngScale = Math.cos((midLat * Math.PI) / 180);

    const spanLat = Math.max(maxLat - minLat, 0.0008);
    const spanLng = Math.max((maxLng - minLng) * lngScale, 0.0008);

    return {
      minLat, maxLat, minLng, maxLng, spanLat, spanLng, lngScale,
      aspect: spanLng / spanLat,
    };
  }, [plotted]);

  if (!plotted.length) {
    return (
      <div className="map-canvas map-empty" style={{ height }}>
        <span className="small faint">No visits with a location on this day.</span>
      </div>
    );
  }

  const g = geometry!;
  // A square viewBox keeps the maths simple; the aspect correction is
  // applied to the coordinates themselves.
  const W = 1000;
  const H = 1000;

  const place = (p: MapPoint) => {
    const x = ((p.longitude - g.minLng) * g.lngScale) / g.spanLng;
    const y = (p.latitude - g.minLat) / g.spanLat;
    // Fit the wider axis and centre the other, so a day spent on one
    // street is not blown up into a continent.
    const scale = Math.min(1 / Math.max(g.aspect, 1), 1);
    const sx = g.aspect >= 1 ? 1 : g.aspect;
    const sy = g.aspect >= 1 ? 1 / g.aspect : 1;
    return {
      x: 60 + (x * sx * scale + (1 - sx * scale) / 2) * (W - 120),
      // SVG y grows downwards; north should be up.
      y: H - 60 - (y * sy * scale + (1 - sy * scale) / 2) * (H - 120),
    };
  };

  const positioned = plotted.map((p) => ({ point: p, ...place(p) }));

  // A scale bar, so "spread out" has a number attached to it.
  const spanKm = Math.max(g.spanLat * 111, g.spanLng * 111);
  const barKm = niceNumber(spanKm / 3);
  const barFraction = barKm / Math.max(spanKm, 0.001);

  return (
    <div className="map-canvas" style={{ height }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" role="img"
        aria-label={`${plotted.length} visits plotted in order`}>
        <defs>
          <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
            <path d="M 50 0 L 0 0 0 50" fill="none" stroke="var(--border)" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width={W} height={H} fill="url(#grid)" />

        {showPath && positioned.length > 1 ? (
          <polyline
            points={positioned.map((p) => `${p.x},${p.y}`).join(' ')}
            fill="none"
            stroke="var(--brand)"
            strokeWidth="3"
            strokeDasharray="8 6"
            opacity="0.55"
          />
        ) : null}

        {positioned.map(({ point, x, y }) => {
          const selected = selectedId === point.visitId;
          return (
            <g
              key={point.visitId}
              transform={`translate(${x} ${y})`}
              onClick={() => onSelect?.(point.visitId)}
              style={{ cursor: onSelect ? 'pointer' : 'default' }}
            >
              <circle
                r={selected ? 22 : 17}
                fill={point.isProductive ? 'var(--success)' : 'var(--brand)'}
                opacity={point.status === 'completed' ? 1 : 0.45}
                stroke="#fff"
                strokeWidth="3"
              />
              <text
                textAnchor="middle"
                dy="6"
                fontSize="16"
                fontWeight="600"
                fill="#fff"
                pointerEvents="none"
              >
                {point.seq}
              </text>
            </g>
          );
        })}

        <g transform={`translate(60 ${H - 26})`}>
          <line
            x1="0" y1="0" x2={Math.max(barFraction * (W - 120), 40)} y2="0"
            stroke="var(--ink-faint)" strokeWidth="3"
          />
          <text x="0" y="-8" fontSize="18" fill="var(--ink-faint)">
            {barKm >= 1 ? `${barKm} km` : `${Math.round(barKm * 1000)} m`}
          </text>
        </g>
      </svg>
    </div>
  );
}

/** 1, 2, 5, 10, 20, 50 … so the scale bar reads like a scale bar. */
function niceNumber(value: number): number {
  if (value <= 0) return 0.1;
  const exponent = Math.floor(Math.log10(value));
  const base = 10 ** exponent;
  const normalised = value / base;
  const step = normalised >= 5 ? 5 : normalised >= 2 ? 2 : 1;
  return Number((step * base).toPrecision(2));
}
